import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { NVButton } from '@nv/basic-components'
import { Downloading, SuccessDownloaded, useNVModal } from '@nv/components'
import { ListTable } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { usePageNumber, usePersistedFiltersMultiSelect, usePersistedSearch } from '@nv/hooks'
import { AdminVoterService } from '@nv/services'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './AdminRegisteredOnlineList.module.scss'
const UserFilters = lazy(() => import('@nv/lazy-loadable/lib/modals/UserFilters'))

const NUM_VOTERS_PER_PAGE = 10

export const AdminRegisteredOnlineList = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [numUsers, setNumUsers] = useState(0)
  const { searchInput, setSearchInput } = usePersistedSearch()
  const { page, handlePageChange } = usePageNumber()
  const { registeredOnlinefilterOptions: filterOptions, includeKnownIdNumber } = useConfigurationContext()

  const { NVModal: UserFiltersModal, openModal: openUserFiltersModal } = useNVModal()
  const { NVModal: SuccessDownloadedModal, openModal: openSuccessDownloadedModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()

  const { filters, handleFilterChange }: any = usePersistedFiltersMultiSelect({
    options: filterOptions,
    onFilterChange: () => handlePageChange(0),
  })

  const columns = useMemo(() => {
    let cols = [
      {
        accessorKey: 'username',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.username'),
      },
      {
        accessorKey: 'voterInfo.fullName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.fullName'),
      },
      {
        accessorKey: 'voterInfo.knownIdNumber',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.knownIdNumber'),
      },
      {
        accessorKey: 'voterInfo.address.fullAddress',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.address'),
      },
      {
        accessorKey: 'voterInfo.dateOfBirth',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.dateOfBirth'),
      },
      {
        accessorKey: 'email',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.email'),
      },
      {
        accessorKey: 'voterInfo.status',
        cell: (info) => (
          <span className={clsx(styles[`status-label`], styles[`status-label__${info.getValue().toLowerCase()}`])}>
            {t(`registration.registrationStatus.${info.getValue().toLowerCase()}`)}
          </span>
        ),
        header: () => t('admin.electorsList.applicationStatus'),
      },
      {
        accessorKey: 'voted',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.votedStatus'),
      },
    ]
    if (
      KeycloakAdminService.isSuperAdmin() ||
      KeycloakAdminService.isContactInfoRoleAdmin() ||
      KeycloakAdminService.isVBMAdmin() ||
      KeycloakAdminService.isMonitoringAdmin()
    ) {
      cols.push({
        accessorKey: 'action',
        cell: (info) => (
          <NVButton
            text={t('main.details')}
            onClick={() => {
              const voterId = info.row.original?.voterInfo?.id
              navigate(`/admin/registrant-details/${voterId}?pageState=${btoa(window.location.pathname + window.location.search)}`)
            }}
          />
        ),
        header: () => t('buttons.action'),
      })
    }
    if (!includeKnownIdNumber) {
      cols = cols.filter((column) => !column.accessorKey.includes('knownIdNumber'))
    }
    return cols
  }, [currentLanguage])

  const getUsers = async () => {
    setLoading(true)
    const arrToStrFilters = (arr) => arr?.map((a) => a.value).join(',')
    const resp = await AdminVoterService.fetchVoters(
      filters.districtFilter
        ? filterOptions.find((f) => f.key === 'districtFilter')?.isMulti
          ? arrToStrFilters(filters.districtFilter)
          : filters.districtFilter.value
        : 'all',
      filters.channelFilter
        ? filterOptions.find((f) => f.key === 'channelFilter')?.isMulti
          ? arrToStrFilters(filters.channelFilter)
          : filters.channelFilter.value
        : 'all',
      NUM_VOTERS_PER_PAGE,
      page * NUM_VOTERS_PER_PAGE,
      searchInput,
      filters.statusFilter
        ? filterOptions.find((f) => f.key === 'statusFilter')?.isMulti
          ? arrToStrFilters(filters.statusFilter)
          : filters.statusFilter.value
        : 'all',
      'name',
      filters.voteByMailFilter
        ? filterOptions.find((f) => f.key === 'voteByMailFilter')?.isMulti
          ? arrToStrFilters(filters.voteByMailFilter)
          : filters.voteByMailFilter.value
        : 'all',
      filters.voteByMailAddressFilter
        ? filterOptions.find((f) => f.key === 'voteByMailAddressFilter')?.isMulti
          ? arrToStrFilters(filters.voteByMailAddressFilter)
          : filters.voteByMailAddressFilter.value
        : 'all',
    )
    if (resp?.users) {
      setUsers(resp.users)
      setNumUsers(resp.numUsers)
    }
    setLoading(false)
  }

  useEffect(() => {
    getUsers()
  }, [page, filters, searchInput])

  const getDowloadUrl = async () => {
    openDownloadingModal()
    const arrToStrFilters = (arr) => arr?.map((a) => a.value).join(',')

    const res = await AdminVoterService.getVoterCsv(
      filters.districtFilter
        ? filterOptions.find((f) => f.key === 'districtFilter')?.isMulti
          ? arrToStrFilters(filters.districtFilter)
          : filters.districtFilter.value
        : 'all',
      filters.channelFilter
        ? filterOptions.find((f) => f.key === 'channelFilter')?.isMulti
          ? arrToStrFilters(filters.channelFilter)
          : filters.channelFilter.value
        : 'all',
    )
    if (res?.success) {
      // @ts-ignore
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `voterlist`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  return (
    <>
      <UserFiltersModal>
        <UserFilters filterOptions={filterOptions} appliedFilters={filters} onFilterChange={handleFilterChange} />
      </UserFiltersModal>

      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <SuccessDownloadedModal>
        <SuccessDownloaded />
      </SuccessDownloadedModal>

      <div className={styles['container']}>
        <ListTable
          data={users}
          columns={columns}
          currentPageNum={page}
          onPageChange={handlePageChange}
          dataLength={numUsers}
          loading={loading}
          showFilter
          onSearch={(input) => {
            setSearchInput(input)
            handlePageChange(0)
          }}
          download={KeycloakAdminService.isVoterAdminDeveloper() ? getDowloadUrl : null}
          searchValue={searchInput}
          setFilterModal={openUserFiltersModal}
          searchPlaceholder='admin.electorsList.search'
          paginationLabel='admin.electorsList.electors'
          numRowsPerPage={NUM_VOTERS_PER_PAGE}
          refreshList={getUsers}
        />
      </div>
    </>
  )
}
